import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import Collapsible from '../../components/Collapsible';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import ClearIcon from '@mui/icons-material/Clear';
import { sortDirection } from '../../components/SortLabel';
import HelpIcon from '@mui/icons-material/Help';

export interface ITimelineOptions {
  filters: string[];
  orderNameBy: sortDirection;
  orderUtilisationBy: sortDirection;
  months: number;
  billable: boolean;
  nonBillable: boolean;
  availabilityDate: Date | null;
  availability: number;
}

interface ITimelineOptionsProps {
  state: ITimelineOptions;
  setState: React.Dispatch<React.SetStateAction<ITimelineOptions>>;
}

function TimelineOptions(props: ITimelineOptionsProps) {
  const { state, setState } = props;
  const { months, billable, nonBillable, availabilityDate, availability } = state;

  const handleMonthsChange = (months: number) => {
    setState({
      ...state,
      months,
    });
  };

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [event.target.name]: event.target.checked,
    });
  };

  const handleAvailabilityChange = (availabilityDate: Date | null) => {
    setState({
      ...state,
      availabilityDate,
    });
  };

  const handleSliderChange = (availability: number) => {
    setState({
      ...state,
      availability,
    });
  };

  return (
    <Collapsible title="More Options">
      <Stack direction="row" spacing={1} justifyContent="space-evenly">
        <Autocomplete
          clearOnEscape
          id="timeline-months"
          options={[1, 2, 3, 4, 5, 6, 12]}
          getOptionLabel={v => v.toString()}
          onChange={(_, option) => handleMonthsChange(option as number)}
          value={months}
          renderInput={params => <TextField {...params} label="Months" />}
        />

        <FormControl component="fieldset" variant="standard" sx={{ display: 'flex' }}>
          <Typography textAlign="center" variant="subtitle1">
            Projects
          </Typography>
          <FormGroup row={true}>
            <FormControlLabel
              control={<Checkbox checked={billable} onChange={handleCheckBoxChange} name="billable" />}
              label="Billable"
            />
            <FormControlLabel
              control={<Checkbox checked={nonBillable} onChange={handleCheckBoxChange} name="nonBillable" />}
              label="Non-billable"
            />
          </FormGroup>
        </FormControl>

        <Stack direction="column" spacing={0.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                clearable
                label="Availability"
                inputFormat="dd/MM/yyyy"
                value={availabilityDate}
                onChange={handleAvailabilityChange}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => handleAvailabilityChange(null)}>
                      <ClearIcon />
                    </IconButton>
                  ),
                }}
                InputAdornmentProps={{
                  position: 'start',
                }}
                renderInput={params => <TextField autoComplete="off" {...params} />}
              />
            </LocalizationProvider>

            <Tooltip title="If an availability date is set, the sidebar will display each employee's current utilisation on the selected day and the slider below can be used to filter employees by their minimum availability (%) on the selected date.">
              <HelpIcon />
            </Tooltip>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'center' }}>
            <Slider
              size="medium"
              value={availability}
              onChange={(_, value) => handleSliderChange(value as number)}
              sx={{ alignItems: 'center', '& .MuiSlider-thumb': { height: '14px', width: '14px' } }}
            />
            <span>{`${availability}%`}</span>
          </Stack>
        </Stack>
      </Stack>
    </Collapsible>
  );
}

export default TimelineOptions;
